import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { QcsCoachMark } from '../../../common/basic/QcsCoachMark';
import { dismissHint } from '../../../../hooks/useHintDismissed';
import { Box, IconButton, Tooltip } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { GridList } from '../../../common/grid/GridList';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { QcsButton } from '../../../common/basic/QcsButton';
import { DocumentInputUploadModal } from '../../../risk/DocumentInputUploadModal';
import { DocumentPreviewModal } from '../../../common/DocumentPreviewModal';
import { DropOverlay } from '../../../common/DropOverlay';
import { ShowImageButton } from './ShowImageButton';
import { useDocumentPreview } from '../../../../hooks/useDocumentPreview';
import { useFileUploadQueue } from '../../../../hooks/useFileUploadQueue';
import { BASE_URL_WITH_VERSION } from '../../../../utils/api';
import { downloadBlob } from '../../../../utils/download';
import { useInvitation } from './invitationFunctions';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole } from '../../../../utils/roles';
import { getMediaToken } from '../../../../utils/storage';
import { FileUploadMediaResponse } from '@api/models/invitation/FileUploadMediaResponse';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { InvitationDocsExportType } from '@api/models/enums/InvitationDocsExportType';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';

const DROP_AREA_ID = 'inv-attachments-drop-area';

export const InvTabAttachments: FC = () => {
  const { t, invitation, callWithLock, reloadInvitation } = useInvitation();
  const identity = useAppSelector(selectIdentity);

  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [hintVisible, setHintVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]) && invitation.state !== InvitationState.Archived;

  useEffect(() => {
    if (!hintVisible) return;
    const handleDragEnter = () => setHintVisible(false);
    window.addEventListener('dragenter', handleDragEnter);
    return () => window.removeEventListener('dragenter', handleDragEnter);
  }, [hintVisible]);

  const preview = useDocumentPreview();

  const onBatchComplete = useCallback(
    async (ids: string[]) => {
      await callWithLock(async () => {
        await invitationService.addAttachment(invitation.id!, { documentIds: ids });
        await reloadInvitation();
      });
    },
    [callWithLock, invitation.id, reloadInvitation],
  );

  const { currentFile, handleFiles, handleFileUploaded, handleFileCancel } = useFileUploadQueue(onBatchComplete);

  const handleDropFiles = useCallback(
    (files: File[]) => {
      dismissHint(DROP_AREA_ID);
      handleFiles(files);
    },
    [handleFiles],
  );

  const handleRowClick = (item: FileUploadMediaResponse) => {
    const url = `${BASE_URL_WITH_VERSION}${item.path}?token=${getMediaToken()}`;
    preview.open(url, { fileName: item.fileName });
  };

  const handleAttachmentDelete = async (item: FileUploadMediaResponse) => {
    await callWithLock(async () => {
      await invitationService.deleteAttachments(invitation.id!, { entries: [item.id!] });
      await reloadInvitation();
    });
    setCheckedItems((prev) => prev.filter((id) => id !== item.id));
  };

  const handleDeleteChecked = async () => {
    if (checkedItems.length === 0) return;
    await callWithLock(async () => {
      await invitationService.deleteAttachments(invitation.id!, { entries: checkedItems });
      await reloadInvitation();
    });
    setCheckedItems([]);
  };

  const handleRenderData = (item: FileUploadMediaResponse) => {
    return (
      <>
        <QcsTableCell>{decodeURIComponent((item.fileName ?? '').replace(/\+/g, ' '))}</QcsTableCell>
        <QcsTableCell padding="none" sx={{ width: 48 }} onClick={(e) => e.stopPropagation()}>
          <ShowImageButton
            tooltipTitle={t('attachment.showDocument')}
            documentUrl={`${BASE_URL_WITH_VERSION}${item.path}?token=${getMediaToken()}`}
            fileName={item.fileName}
            icon={<VisibilityIcon sx={{ color: 'black' }} />}
          />
        </QcsTableCell>
        {canEdit && (
          <QcsTableCell padding="none" sx={{ width: 48 }}>
            <Tooltip title={t('attachment.deleteDocument')} placement="top">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAttachmentDelete(item);
                }}
              >
                <DeleteForeverIcon color="error" />
              </IconButton>
            </Tooltip>
          </QcsTableCell>
        )}
      </>
    );
  };

  const handleDownload = async () => {
    const res = await invitationService.generateZipFile(invitation.id!, checkedItems, InvitationDocsExportType.Attachment);
    await downloadBlob(res);
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
    e.target.value = '';
  };

  return (
    <>
      <DocumentPreviewModal iframeSrc={preview.iframeSrc} contentType={preview.contentType} onClose={preview.close} onDownload={preview.download} />
      <DocumentInputUploadModal file={currentFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInputChange} />

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '1rem' }}>
          <QcsButton variant="contained" onClick={handleAddClick} onMouseEnter={() => setHintVisible(true)}>
            {t('common.add')}
          </QcsButton>
        </Box>
      )}

      <Box id="inv-attachments-drop-area" sx={{ position: 'relative' }}>
        <DropOverlay onFiles={handleDropFiles} disabled={!canEdit}>
          <Box sx={{ '& tbody .MuiTableRow-root': { cursor: 'pointer' } }}>
            <GridList<FileUploadMediaResponse>
              headers={[{ captionStr: 'invitation.attachment.name' }, { sx: { width: 48 } }, canEdit ? { sx: { width: 48 } } : undefined]}
              data={invitation.attachments ?? []}
              renderData={handleRenderData}
              onRowClick={handleRowClick}
              search={false}
              hidePagination={true}
              checkbox={canEdit}
              checkedItems={checkedItems}
              changeChecked={setCheckedItems}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: '1.5rem' }}>
              <QcsLoadingButton
                variant="contained"
                onClick={handleDownload}
                resourceLoading={resources.generateZipFile}
                disabled={checkedItems.length === 0}
              >
                {t('invitation.attachment.download')}
              </QcsLoadingButton>
              {canEdit && (
                <QcsLoadingButton
                  variant="contained"
                  color="error"
                  onClick={handleDeleteChecked}
                  disabled={checkedItems.length === 0}
                  resourceLoading={resources.generateZipFile}
                >
                  {t('common.delete')} ({checkedItems.length})
                </QcsLoadingButton>
              )}
            </Box>
          </Box>
        </DropOverlay>
        <QcsCoachMark
          anchorId="inv-attachments-drop-area"
          open={canEdit && hintVisible}
          icon={<CloudUploadOutlinedIcon sx={{ color: 'primary.main', fontSize: 24, flexShrink: 0 }} />}
          message={t('common.coachMarkDragDrop')}
          delay={0}
          zIndex={5}
        />
      </Box>
    </>
  );
};
